import { useCallback, useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Plus, Trash2, RefreshCcw, Pause, Play, Copy, ShieldCheck,
  FlaskConical, Globe2, KeyRound, X,
} from "lucide-react";

const SCOPES = ["verify", "create", "endorse", "webhooks"];

export default function AdminPartners() {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [credsFor, setCredsFor] = useState(null); // {client_id, client_secret, name}

  const load = useCallback(async () => {
    try { setRows((await api.get("/admin/partners")).data.rows); }
    catch (e) { console.error("load partners failed", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    try { await api.patch(`/admin/partners/${id}`, body); load(); toast.success("Updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const rotate = async (id, name) => {
    if (!confirm(`Rotate secret for "${name}"? The old secret will stop working immediately.`)) return;
    try {
      const { data } = await api.post(`/admin/partners/${id}/rotate-secret`);
      setCredsFor({ ...data, name });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const revoke = async (id, name) => {
    if (!confirm(`Revoke partner "${name}"? Their key will be rejected on the next call.`)) return;
    try { await api.delete(`/admin/partners/${id}`); load(); toast.success("Revoked"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AdminLayout title="Partner API" subtitle="Phase 1 · HMAC bridge for third-party sites">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-zinc-400">
            Grant partner platforms (e.g. coyoin.com) scoped access to search, create and endorse
            EthicCred passports. Every call is HMAC-signed and domain-locked.
          </p>
        </div>
        <Button onClick={() => setShowNew(true)} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" data-testid="partners-new">
          <Plus className="w-4 h-4" />New partner
        </Button>
      </div>

      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="partners-table">
            <thead className="bg-white/5">
              <tr className="text-left text-zinc-500 eyebrow">
                <th className="px-4 py-3">Partner</th>
                <th className="px-4 py-3">Client ID</th>
                <th className="px-4 py-3">Scopes</th>
                <th className="px-4 py-3">Domains</th>
                <th className="px-4 py-3">Calls</th>
                <th className="px-4 py-3">Last used</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-white/5 align-top" data-testid={`partner-row-${p.slug}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {p.sandbox && <FlaskConical className="w-3.5 h-3.5 text-amber-400" title="Sandbox" />}
                      <div>
                        <div className="font-display font-bold text-white">{p.name}</div>
                        <div className="font-mono-plex text-[11px] text-zinc-500">{p.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono-plex text-xs text-zinc-300">
                    <button onClick={() => { navigator.clipboard.writeText(p.client_id); toast.success("Copied"); }} className="inline-flex items-center gap-1 hover:text-white" title="Copy">
                      {p.client_id.slice(0, 18)}… <Copy className="w-3 h-3" />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {SCOPES.map((s) => (
                        <ScopePill key={s} active={!!p.scopes?.[s]} label={s} onClick={() => patch(p.id, { scopes: { ...p.scopes, [s]: !p.scopes?.[s] } })} testid={`partner-${p.slug}-scope-${s}`} />
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-300 max-w-[180px]">
                    <div className="flex flex-wrap gap-1">
                      {(p.domains || []).map((d) => <span key={d} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white/5 border border-white/10 rounded-sm font-mono-plex text-[10px]"><Globe2 className="w-3 h-3" />{d}</span>)}
                      {(p.domains || []).length === 0 && <span className="italic text-zinc-500">any</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono-plex text-xs text-white">{p.call_count || 0}</td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{p.last_used_at ? new Date(p.last_used_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3">
                    <StatusDot status={p.status} />
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {p.status === "active" ? (
                      <IconBtn title="Pause" onClick={() => patch(p.id, { status: "paused" })} data-testid={`partner-${p.slug}-pause`}><Pause className="w-3.5 h-3.5" /></IconBtn>
                    ) : p.status === "paused" ? (
                      <IconBtn title="Resume" onClick={() => patch(p.id, { status: "active" })} data-testid={`partner-${p.slug}-resume`}><Play className="w-3.5 h-3.5" /></IconBtn>
                    ) : null}
                    <IconBtn title="Rotate secret" onClick={() => rotate(p.id, p.name)} data-testid={`partner-${p.slug}-rotate`}><RefreshCcw className="w-3.5 h-3.5" /></IconBtn>
                    {p.status !== "revoked" && (
                      <IconBtn danger title="Revoke" onClick={() => revoke(p.id, p.name)} data-testid={`partner-${p.slug}-revoke`}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500 italic text-xs">No partners yet. Create one to issue a client_id and client_secret.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {showNew && <NewPartnerModal onClose={() => setShowNew(false)} onCreated={(c) => { setShowNew(false); setCredsFor(c); load(); }} />}
      {credsFor && <CredentialsModal creds={credsFor} onClose={() => setCredsFor(null)} />}
    </AdminLayout>
  );
}

function IconBtn({ children, danger, ...rest }) {
  return (
    <button
      {...rest}
      className={`p-1.5 border rounded-sm ml-1 ${danger ? "border-ec-error/30 text-ec-error hover:bg-ec-error/10" : "border-white/10 text-zinc-300 hover:bg-white/5"}`}
    >{children}</button>
  );
}

function ScopePill({ active, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm font-mono-plex transition-colors ${active ? "border-ec-emerald/50 bg-ec-emerald/15 text-ec-emerald" : "border-white/10 bg-white/5 text-zinc-500 line-through"}`}
    >{label}</button>
  );
}

function StatusDot({ status }) {
  const map = {
    active: ["bg-ec-emerald", "Active"],
    paused: ["bg-amber-400", "Paused"],
    revoked: ["bg-ec-error", "Revoked"],
  };
  const [bg, label] = map[status] || ["bg-zinc-500", status];
  return <span className="inline-flex items-center gap-1.5 text-xs text-zinc-200"><span className={`w-2 h-2 rounded-full ${bg}`} />{label}</span>;
}

function NewPartnerModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    name: "", slug: "", domains: "",
    scopes: { verify: true, create: false, endorse: false, webhooks: false },
    sandbox: false, webhook_url: "",
  });
  const save = async () => {
    try {
      const body = {
        ...f,
        slug: f.slug || f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        domains: f.domains.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.post("/admin/partners", body);
      toast.success("Partner created");
      onCreated({ client_id: data.client_id, client_secret: data.client_secret, name: data.partner.name });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={onClose} data-testid="partner-new-modal">
      <div className="bg-ec-ink border border-white/10 rounded-sm max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-xl font-bold text-white mb-5">New partner</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-1"><Label className="eyebrow text-zinc-500">Name</Label><Input className="input-dark mt-2" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="CoyoIN" data-testid="partner-input-name" /></div>
          <div className="col-span-1"><Label className="eyebrow text-zinc-500">Slug</Label><Input className="input-dark mt-2 font-mono-plex" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} placeholder="coyoin" data-testid="partner-input-slug" /></div>
          <div className="col-span-2"><Label className="eyebrow text-zinc-500">Allowed domains (comma-separated)</Label><Input className="input-dark mt-2 font-mono-plex" value={f.domains} onChange={(e) => setF({ ...f, domains: e.target.value })} placeholder="coyoin.com, staging.coyoin.com" data-testid="partner-input-domains" /></div>
          <div className="col-span-2"><Label className="eyebrow text-zinc-500">Webhook URL (optional)</Label><Input className="input-dark mt-2 font-mono-plex" value={f.webhook_url} onChange={(e) => setF({ ...f, webhook_url: e.target.value })} placeholder="https://coyoin.com/webhooks/ethiccred" data-testid="partner-input-webhook" /></div>
          <div className="col-span-2">
            <Label className="eyebrow text-zinc-500 block mb-2">Scopes</Label>
            <div className="grid grid-cols-2 gap-2">
              {SCOPES.map((s) => (
                <label key={s} className="flex items-center gap-2 border border-white/10 bg-white/5 px-3 py-2 rounded-sm">
                  <Switch checked={f.scopes[s]} onCheckedChange={(v) => setF({ ...f, scopes: { ...f.scopes, [s]: v } })} data-testid={`partner-input-scope-${s}`} />
                  <span className="text-sm text-white capitalize">{s}</span>
                </label>
              ))}
            </div>
          </div>
          <label className="col-span-2 flex items-center gap-2">
            <Switch checked={f.sandbox} onCheckedChange={(v) => setF({ ...f, sandbox: v })} data-testid="partner-input-sandbox" />
            <span className="text-sm text-zinc-300">Sandbox mode (writes stay isolated for safe testing)</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" className="rounded-sm border-white/10 text-white" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="rounded-sm bg-ec-emerald text-ec-ink" data-testid="partner-save">Create & show credentials</Button>
        </div>
      </div>
      <style>{`.input-dark { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.1); color: #fff; border-radius: 2px; }`}</style>
    </div>
  );
}

function CredentialsModal({ creds, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={onClose} data-testid="partner-creds-modal">
      <div className="bg-ec-ink border border-ec-emerald/40 rounded-sm max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="inline-flex items-center gap-2 text-ec-emerald"><KeyRound className="w-4 h-4" /><span className="font-display font-bold">Save these credentials now</span></div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-zinc-300 mb-4">
          For <span className="font-bold text-white">{creds.name}</span>. The <span className="font-bold">client_secret</span> is shown <span className="text-ec-error">only once</span>. Store it in a vault.
        </p>
        <CredField label="client_id" value={creds.client_id} />
        <CredField label="client_secret" value={creds.client_secret} secret />
        <div className="mt-4 p-3 bg-amber-400/10 border border-amber-400/30 rounded-sm text-xs text-amber-200 flex gap-2">
          <ShieldCheck className="w-4 h-4 shrink-0" />
          Share only via a secure channel. If leaked, rotate the secret from this page.
        </div>
        <div className="flex justify-end mt-5">
          <Button onClick={onClose} className="rounded-sm bg-ec-emerald text-ec-ink">I've saved them</Button>
        </div>
      </div>
    </div>
  );
}

function CredField({ label, value }) {
  const [shown, setShown] = useState(false);
  const copy = () => { navigator.clipboard.writeText(value); toast.success("Copied"); };
  return (
    <div className="mb-3">
      <div className="eyebrow text-zinc-500 mb-1">{label}</div>
      <div className="flex items-stretch">
        <div className="flex-1 font-mono-plex text-sm text-white bg-white/5 border border-white/10 px-3 py-2 rounded-sm break-all">
          {shown ? value : "•".repeat(Math.min(40, value.length))}
        </div>
        <Button variant="outline" className="rounded-sm border-white/10 text-white ml-2" onClick={() => setShown((v) => !v)}>{shown ? "Hide" : "Show"}</Button>
        <Button variant="outline" className="rounded-sm border-white/10 text-white ml-2" onClick={copy}><Copy className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );
}
